// SIGN IN (UI rebuild batch 5, Astra's `login`) — the web console's sign-in, drawn where WebLogin is (App's auth guard with onAuthed, and /login).
// Same props, same flow, line for line (plan verification 3.5): the console mints a nonce (POST /api/auth/device-start), deep-links into the ops bot,
// shows the ANTI-PHISHING CONFIRMATION CODE — prominent: he matches it with the one the bot shows — and polls GET /api/auth/device-poll until he
// approves in Telegram (approved → the session; denied / expired → a deliberate Try again). The fallback is the official Telegram Login Widget
// (POST /api/auth/telegram-widget). Both mint the same 7-day session token. Never reachable from initData context — App mounts it outside Telegram.
//
// Astra's layout: a narrow panel under "Sign in to the Books", one action, the request's state and its expiry, and the request → approve → signed in
// progression said as three short steps. Astra's "Telegram username" field is NOT a step of this flow and is not drawn (verification 3.5). The
// troubleshooting — the widget — is one quiet link, the only door to it.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type TelegramWidgetPayload } from '../../lib/api';
import { errorWords } from '../../lib/errors';
import { setWebSession } from '../../lib/webSession';
import { StateLine } from '../../components/v2/kit';
import { failLine } from '../../components/OnboardingParts';

const BOT_USERNAME = import.meta.env.VITE_TG_BOT_USERNAME ?? 'DubaiOTC_Ops_Bot';

const POLL_INTERVAL_MS = 2000;

type DevicePhase = 'idle' | 'starting' | 'waiting' | 'denied' | 'expired';

// Anti-phishing confirmation code. MUST match the derivation in bff/src/routes/bot.ts (and screens/WebLogin.tsx) — the bot's approval message shows
// the same code, so the operator can tell this browser's request apart from a deep link someone else minted and sent to them.
function loginConfirmCode(nonce: string): string {
  return nonce.slice(0, 6).toUpperCase();
}

/** A sign-in that failed, as ONE red line: what could not be done and the server's first sentence when that is short; a long one is behind Details. */
function SignInFailed({ what, error }: { what: string; error: string }) {
  const { line, more } = failLine(`Couldn't ${what}`, error);
  return <StateLine tone="bad" className="w-full" more={more}>{line}</StateLine>;
}

function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramWidgetPayload) => void;
  }
}

/** Astra's progression, three short steps: the one he is on is lit; a step behind him is done. */
function Steps({ at }: { at: 0 | 1 }) {
  const steps = ['Request', 'Approve in Telegram', 'Signed in'];
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-meta" aria-label="Sign-in steps">
      {steps.map((s, i) => (
        <li key={s} className="flex items-center gap-2" aria-current={i === at ? 'step' : undefined}>
          {i > 0 && <span className="text-tg-hint" aria-hidden>→</span>}
          <span className={i === at ? 'font-medium text-tg-text' : i < at ? 'text-pos' : 'text-tg-hint'}>{s}</span>
        </li>
      ))}
    </ol>
  );
}

export function SignInV2({ onAuthed }: { onAuthed?: () => void }) {
  const navigate = useNavigate();

  // Bot-approval flow
  const [phase, setPhase] = useState<DevicePhase>('idle');
  const [nonce, setNonce] = useState<string | null>(null);
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [deviceError, setDeviceError] = useState<string | null>(null);

  // Widget fallback
  const widgetRef = useRef<HTMLDivElement>(null);
  const [showWidget, setShowWidget] = useState(false);
  const [widgetBusy, setWidgetBusy] = useState(false);
  const [widgetError, setWidgetError] = useState<string | null>(null);

  const startDeviceLogin = async () => {
    setPhase('starting');
    setDeviceError(null);
    try {
      const res = await api.authDeviceStart();
      setNonce(res.nonce);
      setDeepLink(res.deep_link);
      setSecondsLeft(res.expires_in);
      setPhase('waiting');
      // Popup blockers may eat this (it follows an await) — the waiting card keeps an explicit "Open Telegram" link as the manual path.
      window.open(res.deep_link, '_blank', 'noopener');
    } catch (e) {
      setPhase('idle');
      // The API says why. Guessing "check your connection" told the operator to fix their wifi when device-start had actually answered db_error — the
      // tunnel to the Mac being down, which no amount of retrying fixes.
      setDeviceError(errorWords(e));
    }
  };

  // Poll while waiting. approved → store the token and enter the console; denied/expired → terminal state with retry. Transient poll failures are
  // swallowed — the countdown is the real deadline. Interval clears on unmount and whenever the phase leaves 'waiting'.
  useEffect(() => {
    if (phase !== 'waiting' || !nonce) return;
    let cancelled = false;
    let inFlight = false;
    const id = window.setInterval(async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await api.authDevicePoll(nonce);
        if (cancelled) return;
        if (res.status === 'approved' && res.token) {
          window.clearInterval(id);
          setWebSession(res.token);
          onAuthed?.();
          navigate('/', { replace: true });
        } else if (res.status === 'denied') {
          setPhase('denied');
        } else if (res.status === 'expired') {
          setPhase('expired');
        }
      } catch {
        // Keep polling — the countdown below expires the attempt.
      } finally {
        inFlight = false;
      }
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, nonce]);

  // Countdown ticks once a second while waiting.
  useEffect(() => {
    if (phase !== 'waiting') return;
    const id = window.setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [phase]);

  // Local expiry — flipping the phase also tears down the poll interval.
  useEffect(() => {
    if (phase === 'waiting' && secondsLeft <= 0) setPhase('expired');
  }, [phase, secondsLeft]);

  // Fallback widget. The script is injected only when the fallback is revealed (one-way disclosure) and must not be re-injected on re-renders.
  useEffect(() => {
    if (!showWidget) return;
    window.onTelegramAuth = async (user) => {
      setWidgetBusy(true);
      setWidgetError(null);
      try {
        const { token } = await api.authTelegramWidget(user);
        setWebSession(token);
        onAuthed?.();
        navigate('/', { replace: true });
      } catch (e) {
        setWidgetBusy(false);
        // Only a 403 means "not a registered operator", and errorWords already says that. Printing it for every failure accused the operator's own
        // account whenever the network dropped or the books were down.
        setWidgetError(errorWords(e));
      }
    };

    const el = widgetRef.current;
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', BOT_USERNAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    el?.appendChild(script);
    return () => {
      delete window.onTelegramAuth;
      el?.replaceChildren();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWidget]);

  return (
    <div className="flex min-h-screen justify-center bg-tg-bg px-4 py-16 lg:items-center lg:py-6">
      <div className="w-full max-w-[420px] space-y-6">
        <div className="space-y-4">
          <div className="grid h-9 w-9 select-none place-items-center rounded-lg border border-tg-accent/60 text-body font-semibold text-tg-accent" aria-hidden>D</div>
          <div>
            <h1 className="text-page font-semibold">Sign in to the Books</h1>
            <p className="mt-2 text-body text-tg-hint">Approve this request in Telegram.</p>
          </div>
        </div>

        <section className="space-y-4 rounded-[10px] border border-stroke bg-panel p-5" aria-live="polite">
          {(phase === 'idle' || phase === 'starting') && (
            <>
              <Steps at={0} />
              <button type="button" className="btn-primary min-h-11 w-full text-body lg:min-h-10" onClick={startDeviceLogin} disabled={phase === 'starting'}>
                {phase === 'starting' ? 'Requesting…' : 'Request access'}
              </button>
              {deviceError && <SignInFailed what="start the sign-in" error={deviceError} />}
            </>
          )}

          {phase === 'waiting' && (
            <>
              <Steps at={1} />
              <div className="rounded-lg border border-stroke bg-panel-2 px-4 py-4 text-center">
                <p className="text-label font-medium uppercase tracking-wider text-tg-hint">Confirmation code</p>
                {nonce && (
                  <p className="mt-2 select-all font-mono text-figure font-semibold tracking-[0.3em] tabular" data-confirm-code="">
                    {loginConfirmCode(nonce)}
                  </p>
                )}
                <p className="mt-2 text-meta text-tg-hint">Only approve if Telegram shows this code.</p>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-body">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-tg-accent" aria-hidden />
                  Waiting for approval
                </span>
                <span className="font-mono text-meta text-tg-hint tabular">Expires in {formatCountdown(secondsLeft)}</span>
              </div>
              {deepLink && (
                <a className="btn-secondary inline-flex min-h-11 w-full items-center justify-center text-body lg:min-h-10" href={deepLink} target="_blank" rel="noreferrer">
                  Open Telegram
                </a>
              )}
            </>
          )}

          {(phase === 'denied' || phase === 'expired') && (
            <>
              <StateLine tone={phase === 'denied' ? 'bad' : 'warn'} className="w-full">
                {phase === 'denied' ? 'The sign-in was denied in Telegram.' : 'The sign-in request expired.'}
              </StateLine>
              <button type="button" className="btn-primary min-h-11 w-full text-body lg:min-h-10" onClick={startDeviceLogin}>
                Try again
              </button>
            </>
          )}
        </section>

        {showWidget ? (
          <section className="flex flex-col items-center gap-3 rounded-[10px] border border-stroke bg-panel p-5">
            <div ref={widgetRef} className={widgetBusy ? 'hidden' : 'min-h-[40px]'} />
            {widgetBusy && <p className="text-body text-tg-hint">Verifying…</p>}
            {widgetError && <SignInFailed what="verify you" error={widgetError} />}
          </section>
        ) : (
          <button type="button" className="min-h-11 text-meta text-tg-hint underline-offset-2 hover:text-tg-text hover:underline lg:min-h-0" onClick={() => setShowWidget(true)}>
            Trouble? Use the Telegram widget
          </button>
        )}
      </div>
    </div>
  );
}
